import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";

export const NOTICE_TYPES = ["सूचना", "आह्वान"];
export const NOTICE_LIMITS = {
  title: 160,
  date: 80,
  content: 10000,
};
const INITIAL_SNAPSHOT_TIMEOUT_MS = 12000;

function getNoticesCollection() {
  if (!db) throw new Error("Firebase configuration is missing.");
  return collection(db, "notices");
}

function subscribeWithInitialTimeout(reference, onSnapshotData, onError) {
  let receivedSnapshot = false;
  const timeoutId = setTimeout(() => {
    if (!receivedSnapshot) {
      const error = new Error("Firestore did not return an initial snapshot.");
      error.code = "unavailable";
      onError(error);
    }
  }, INITIAL_SNAPSHOT_TIMEOUT_MS);

  try {
    const unsubscribe = onSnapshot(
      reference,
      { includeMetadataChanges: true },
      (snapshot) => {
        receivedSnapshot = true;
        clearTimeout(timeoutId);
        onSnapshotData(snapshot);
      },
      (error) => {
        clearTimeout(timeoutId);
        onError(error);
      },
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

function timestampValue(timestamp) {
  return timestamp && typeof timestamp.toMillis === "function"
    ? timestamp.toMillis()
    : 0;
}

function normalizeNotice(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    title: typeof data.title === "string" ? data.title : "",
    type: NOTICE_TYPES.includes(data.type) ? data.type : "सूचना",
    date: typeof data.date === "string" ? data.date : "",
    content: typeof data.content === "string" ? data.content : "",
    important: data.important === true,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

export function subscribeToNotices(onData, onError) {
  return subscribeWithInitialTimeout(
    getNoticesCollection(),
    (snapshot) => {
      const notices = snapshot.docs.map(normalizeNotice);
      onData(
        notices.sort(
          (a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt),
        ),
        { fromCache: snapshot.metadata.fromCache },
      );
    },
    onError,
  );
}

export function subscribeToNotice(noticeId, onData, onError) {
  if (!db) throw new Error("Firebase configuration is missing.");
  return subscribeWithInitialTimeout(
    doc(db, "notices", noticeId),
    (snapshot) => {
      onData(snapshot.exists() ? normalizeNotice(snapshot) : null, {
        fromCache: snapshot.metadata.fromCache,
      });
    },
    onError,
  );
}

export function validateNotice(values) {
  const errors = {};
  const title = values.title.trim();
  const date = values.date.trim();
  const content = values.content.trim();

  if (!title) errors.title = "शीर्षक आवश्यक छ।";
  else if (title.length > NOTICE_LIMITS.title)
    errors.title = `शीर्षक ${NOTICE_LIMITS.title} अक्षरभन्दा छोटो हुनुपर्छ।`;
  if (!NOTICE_TYPES.includes(values.type))
    errors.type = "सूचनाको प्रकार छान्नुहोस्।";
  if (!date) errors.date = "मिति आवश्यक छ।";
  else if (date.length > NOTICE_LIMITS.date)
    errors.date = `मिति ${NOTICE_LIMITS.date} अक्षरभन्दा छोटो हुनुपर्छ।`;
  if (!content) errors.content = "विवरण आवश्यक छ।";
  else if (content.length > NOTICE_LIMITS.content)
    errors.content = `विवरण ${NOTICE_LIMITS.content} अक्षरभन्दा छोटो हुनुपर्छ।`;

  return {
    errors,
    value: {
      title,
      type: values.type,
      date,
      content,
      important: values.important === true,
    },
  };
}

export async function createNotice(values, user) {
  const { errors, value } = validateNotice(values);
  if (Object.keys(errors).length)
    throw new Error("कृपया सूचना फारम जाँच गर्नुहोस्।");
  if (!user || !user.uid) throw new Error("प्रमाणित प्रशासक आवश्यक छ।");

  return addDoc(getNoticesCollection(), {
    ...value,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user.uid,
    updatedBy: user.uid,
  });
}

export async function updateNotice(noticeId, values, user) {
  const { errors, value } = validateNotice(values);
  if (Object.keys(errors).length)
    throw new Error("कृपया सूचना फारम जाँच गर्नुहोस्।");
  if (!user || !user.uid) throw new Error("प्रमाणित प्रशासक आवश्यक छ।");
  if (!db) throw new Error("Firebase configuration is missing.");

  return updateDoc(doc(db, "notices", noticeId), {
    ...value,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}

export async function deleteNotice(noticeId) {
  if (!db) throw new Error("Firebase configuration is missing.");
  return deleteDoc(doc(db, "notices", noticeId));
}
